import { Button, VStack, useDisclosure, useToast } from '@chakra-ui/react';
import { ArchiveQuality } from './ArchiveQuality';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { qualityTableColumns } from './Columns';
import { EditQuality } from './EditQuality';
import { CreateQuality } from './CreateQuality';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import {
  QualityListItem,
  qualitiesApi,
} from '@texas/api/endpoints/metadata/qualitiesApi';
import { DataTableContainer } from '@texas/components/shared/dataTable/DataTableContainer';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { ServerError } from '@texas/types';
import { request } from '@texas/utils/helpers/httpHelpers';
import { ConfigurationPage } from '@texas/components/configuration/layout/ConfigurationPage';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { TexasSwitch } from '@texas/components/shared/form/TexasSwitch';
import {
  SearchQuery,
  defaultSearchQuery,
} from '@texas/api/endpoints/metadata/types';
import { SelectProductGroup } from '@texas/components/shared/form/SelectProductGroup';

export function Qualities() {
  const {
    onOpen: onCreateOpen,
    onClose: onCreateClose,
    isOpen: isCreateOpen,
  } = useDisclosure();
  const {
    value: archiveValue,
    isOpen: isArchiveOpen,
    onOpen: onArchiveOpen,
    onClose: onArchiveClose,
  } = useValueDisclosure<QualityListItem>();
  const {
    value: editValue,
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useValueDisclosure<number>();

  const { t } = useTranslation();
  const toast = useToast();

  const {
    data: qualities,
    refetch,
    error,
    loading,
  } = useApiResource(qualitiesApi.getAll);

  const [searchQuery, setSearchQuery] =
    useState<SearchQuery>(defaultSearchQuery);

  const { request: restoreQualityRequest } = useApiRequest(
    qualitiesApi.restore,
  );

  useEffect(() => {
    refetch(searchQuery);
  }, [refetch, searchQuery]);

  const performRestore = useCallback(
    async (id: number) => {
      await request(
        restoreQualityRequest,
        [id],
        () => {
          toast({
            title: t('general.restored'),
            status: 'success',
            isClosable: true,
          });
          refetch(searchQuery);
        },
        (error: ServerError) => {
          toast({
            title: t('general.restoreFailed'),
            description: error.message,
            status: 'error',
            isClosable: true,
          });
        },
      );
    },
    [restoreQualityRequest, toast, t, refetch, searchQuery],
  );

  const columns = useMemo(() => {
    return qualityTableColumns(
      t,
      onArchiveOpen,
      performRestore,
      onEditOpen,
      searchQuery.productGroupId ?? 0,
    );
  }, [
    t,
    onArchiveOpen,
    performRestore,
    onEditOpen,
    searchQuery.productGroupId,
  ]);

  return (
    <ConfigurationPage
      title={t('configuration.qualities')}
      toolbar={
        <>
          <SearchFilterInput
            value={searchQuery.searchTerm}
            style="entityDirectory"
            onChange={(s) => setSearchQuery({ ...searchQuery, searchTerm: s })}
          />
          <SelectProductGroup
            selectedProductGroupId={searchQuery.productGroupId}
            onSelect={(id) =>
              setSearchQuery({
                ...searchQuery,
                productGroupId: id > 0 ? id : null,
              })
            }
          />
          <TexasSwitch
            checked={searchQuery.includeArchived}
            label={t('general.includeArchived')}
            onChange={(checked) =>
              setSearchQuery({
                ...searchQuery,
                includeArchived: checked,
              })
            }
          />
        </>
      }
      action={
        <Button variant="entityDirectoryPrimary" onClick={onCreateOpen}>
          {t('general.addNew')}
        </Button>
      }
    >
      {archiveValue && (
        <ArchiveQuality
          quality={archiveValue}
          isOpen={isArchiveOpen}
          onClose={onArchiveClose}
          onArchive={() => {
            refetch(searchQuery);
          }}
        />
      )}

      <EditQuality
        qualityId={editValue}
        isOpen={isEditOpen}
        onClose={onEditClose}
        onUpdated={(_) => {
          refetch(searchQuery);
        }}
      />

      <CreateQuality
        onCreated={() => {
          refetch(searchQuery);
          onCreateClose();
        }}
        onClose={onCreateClose}
        isOpen={isCreateOpen}
      />

      <VStack w="100%" alignItems="start">
        <DataTableContainer
          w="100%"
          datatable={{
            data: qualities ?? [],
            sorted: {
              onSortedChange: ({ key, desc }) => {
                setSearchQuery({ ...searchQuery, sortBy: key, sortDesc: desc });
              },
              key: searchQuery.sortBy,
              desc: searchQuery.sortDesc,
            },
            isLoading: loading,
            columns: columns,
            variant: 'entity-directory',
          }}
          error={error}
        />
      </VStack>
    </ConfigurationPage>
  );
}
